/** @flow */

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import {
  GetEmployeeIdentityData,
  UpdatePlatformDataSecurityEmployeeIdentity,
  GetPlatformDataSecurityEmployeeIdentitySchema,
} from "@modules/service/constants/actions";
import type { EmployeeIdentity } from "@modules/service/reducer/employee-identity/types";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { removeNullAndUndefinedProps } from "@transformers/object";
import { parseSchema } from "@utils/schema";

type EmployeeIdentityDataRequest = $Exact<{
  clientId: string,
  notes: string,
}>;

type UpdatePlatformDataSecurityEmployeeIdentityRequest = $Exact<{
  formData: EmployeeIdentity,
  clientId: string,
  formData: Object,
}>;

export function* onGetEmployeeIdentityData({
  payload,
}: Action<EmployeeIdentityDataRequest>): Generator<*, void, { data: EmployeeIdentity }> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.get,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-employee-identity`,
    );

    yield put(
      actionCreators.GetEmployeeIdentityDataSuccess(removeNullAndUndefinedProps(response.data)),
    );
  } catch (e) {
    yield put(actionCreators.GetEmployeeIdentityDataError(e.response));
  }
}

export function* onGetPlatformDataSecurityEmployeeIdentitySchema(): Generator<*, void, Object> {
  try {
    const data = yield parseSchema("DataSecurityEmployeeIdentityServiceSectionDTO");

    yield put(actionCreators.GetPlatformDataSecurityEmployeeIdentitySchemaSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetPlatformDataSecurityEmployeeIdentitySchemaError(e.response));
  }
}

export function* onUpdatePlatformDataSecurityEmployeeIdentity({
  payload,
}: Action<UpdatePlatformDataSecurityEmployeeIdentityRequest>): Generator<
  *,
  void,
  { data: EmployeeIdentity },
> {
  try {
    if (!payload || !payload.clientId || !payload.formData) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(
      http.put,
      `/api/v2/service-admin/client/${payload.clientId}/service-config/platform/data-security-employee-identity`,
      payload.formData,
    );

    yield all([
      put(actionCreators.UpdatePlatformDataSecurityEmployeeIdentitySuccess(response.data)),
      put(sendSuccessMessage("EMPLOYEE_IDENTITY_SETTINGS_UPDATED")),
      put(actionCreators.GetEmployeeIdentityData({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdatePlatformDataSecurityEmployeeIdentityError(e.response));
  }
}

export function* EmployeeIdentifySagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetEmployeeIdentityData, onGetEmployeeIdentityData),
    takeEvery(
      UpdatePlatformDataSecurityEmployeeIdentity,
      onUpdatePlatformDataSecurityEmployeeIdentity,
    ),
    takeEvery(
      GetPlatformDataSecurityEmployeeIdentitySchema,
      onGetPlatformDataSecurityEmployeeIdentitySchema,
    ),
  ]);
}

export default EmployeeIdentifySagas;
